import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { PhoneField, type PhoneFieldValue } from "@/components/auth/PhoneField";
import { AngaMark } from "@/components/brand/AngaMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError, authApi, persistSession } from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";
import { useOnboardingStore } from "@/stores/onboarding";

export const Route = createFileRoute("/signup")({
  validateSearch: (search: Record<string, unknown>): { invite?: string } => {
    const invite = typeof search["invite"] === "string" ? search["invite"] : undefined;
    return invite ? { invite } : {};
  },
  head: () => ({
    meta: [
      { title: "Create account" },
      { name: "description", content: "Create a resident account for local flood alerts." },
    ],
  }),
  component: SignupPage,
});

function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { platformName, googleEnabled, config } = usePlatformConfig();
  const search = Route.useSearch();
  const inviteFromUrl = search.invite?.trim() || "";
  const setInviteCode = useOnboardingStore((s) => s.setInviteCode);
  const storedInvite = useOnboardingStore((s) => s.inviteCode);
  const [inviteInput, setInviteInput] = useState(inviteFromUrl || storedInvite || "");
  const signupMode = String(config?.signupMode || config?.features?.signupMode || "open");
  const inviteRequired = signupMode === "invite";
  const inviteCode = (inviteFromUrl || inviteInput || storedInvite || "").trim();

  useEffect(() => {
    if (inviteFromUrl) setInviteCode(inviteFromUrl);
  }, [inviteFromUrl, setInviteCode]);

  const setPhone = useOnboardingStore((s) => s.setPhone);
  const setCountry = useOnboardingStore((s) => s.setCountry);
  const setName = useOnboardingStore((s) => s.setName);
  const setEmailStore = useOnboardingStore((s) => s.setEmail);
  const language = useOnboardingStore((s) => s.language);

  const [name, setNameLocal] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phoneValue, setPhoneValue] = useState<PhoneFieldValue>({
    e164: "",
    national: "",
    country: "KE",
    valid: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [phoneInUse, setPhoneInUse] = useState(false);
  const [loading, setLoading] = useState(false);

  function clearErrors() {
    setError(null);
    setPhoneInUse(false);
  }

  function handleApiError(err: unknown) {
    if (err instanceof ApiError && err.code === "PHONE_IN_USE") {
      setPhoneInUse(true);
      setError("This phone number already has an account.");
      return;
    }
    setError(err instanceof ApiError ? err.message : t("common.errorGeneric"));
  }

  async function onEmailSubmit(event: FormEvent) {
    event.preventDefault();
    clearErrors();
    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!email.trim()) {
      setError("Please enter your email.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!phoneValue.valid || !phoneValue.e164) {
      setError("Enter a valid phone number, for example +254712345678.");
      return;
    }
    setLoading(true);
    try {
      if (inviteRequired && !inviteCode) {
        setError("An invite code is required to create an account.");
        setLoading(false);
        return;
      }
      if (inviteCode) setInviteCode(inviteCode);
      const payload: Parameters<typeof authApi.register>[0] = {
        name: name.trim(),
        email: email.trim(),
        password,
        language: language === "sw" ? "SW" : "EN",
        phone: phoneValue.e164,
        country: phoneValue.country,
        channel: "email",
      };
      if (inviteCode) payload.inviteCode = inviteCode;
      const result = await authApi.register(payload);
      persistSession("user", {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken ?? null,
      });
      setName(name.trim());
      setEmailStore(email.trim());
      setPhone(payload.phone);
      setCountry(phoneValue.country);
      window.location.assign(result.dashboard || "/onboarding/persona");
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary-soft),_transparent_55%)]" />
      <div className="relative w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm md:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#064E3B] text-white">
            <AngaMark className="h-6 w-6" title={platformName} />
          </div>
          <div>
            <p className="font-display text-lg font-bold leading-none">{platformName}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              {t("auth.signupTitle")}
            </p>
          </div>
        </div>

        {inviteCode && inviteFromUrl ? (
          <p
            className="mb-4 rounded-md border border-primary/20 bg-primary-soft px-3 py-2 text-xs text-foreground"
            data-testid="pilot-invite-note"
          >
            Pilot invite enrollment — code <span className="font-mono font-bold">{inviteCode}</span>
            . You are joining a limited advisory cohort. Official emergency instructions always take
            priority.
          </p>
        ) : null}

        {inviteRequired && !inviteFromUrl ? (
          <label className="mb-4 block text-xs font-semibold">
            Invite code
            <Input
              className="mt-2"
              value={inviteInput}
              onChange={(e) => setInviteInput(e.target.value.toUpperCase())}
              placeholder="Enter invite code"
              autoComplete="off"
              data-testid="signup-invite-input"
            />
          </label>
        ) : null}

        <Button
          type="button"
          variant="outline"
          className="w-full bg-white text-foreground hover:bg-white"
          disabled={!googleEnabled || loading}
          onClick={() => {
            window.location.href = authApi.googleStartUrl("user");
          }}
        >
          <GoogleMark />
          Sign up with Google
        </Button>
        {!googleEnabled ? (
          <p className="mt-2 text-center text-xs text-muted-foreground">{t("common.googleDisabled")}</p>
        ) : null}

        <div className="my-5 flex items-center gap-3">
          <span className="h-px flex-1 bg-border" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Or sign up with email</span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <form className="space-y-4" onSubmit={onEmailSubmit}>
          <label className="block text-xs font-semibold">
            {t("common.name")}
            <span className="text-severe"> *</span>
            <Input
              required
              value={name}
              onChange={(e) => {
                clearErrors();
                setNameLocal(e.target.value);
              }}
              className="mt-2 h-10"
              autoComplete="name"
              name="name"
            />
          </label>
          <label className="block text-xs font-semibold">
            {t("common.email")}
            <span className="text-severe"> *</span>
            <Input
              required
              type="email"
              value={email}
              onChange={(e) => {
                clearErrors();
                setEmail(e.target.value);
              }}
              className="mt-2 h-10"
              autoComplete="email"
              name="email"
            />
          </label>
          <label className="block text-xs font-semibold">
            {t("common.password")}
            <span className="text-severe"> *</span>
            <div className="relative mt-2">
              <Input
                required
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  clearErrors();
                  setPassword(e.target.value);
                }}
                minLength={8}
                className="h-10 pr-10"
                autoComplete="new-password"
                name="password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>
          <PhoneField
            required
            value={phoneValue}
            onChange={(v) => {
              clearErrors();
              setPhoneValue(v);
            }}
            label="Phone"
            placeholder="712 345 678"
          />
          {error ? (
            <p className="rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
              {error}
              {phoneInUse ? (
                <>
                  {" "}
                  <Link to="/login" className="font-semibold underline">
                    Sign in
                  </Link>
                </>
              ) : null}
            </p>
          ) : null}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? t("common.loading") : t("common.signup")}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t("onboarding.signup.haveAccount")}{" "}
          <Link to="/login" className="text-primary hover:underline">
            {t("common.login")}
          </Link>
          {" · "}
          <Link to="/" className="text-primary hover:underline">
            {t("common.portal")}
          </Link>
        </p>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.5 5.7-6.6 7.1l.1.1 6.3 5.3C36.9 41.6 44 36 44 24c0-1.2-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}
